import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppTile } from '../../components/AppTile';
import { Skeleton } from '../../components/Skeleton';
import type { AppRecord } from '../../lib/types';

interface PinnedAppsProps {
  pinnedApps: AppRecord[];
  loading: boolean;
  busyId: string | null;
  readOnly: boolean;
  onTogglePin: (app: AppRecord) => void;
  onRequestAccess: (app: AppRecord | null) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SortablePinnedTile = ({
  app,
  onTogglePin,
  onRequestAccess,
  busy,
  preview,
}: {
  app: AppRecord;
  onTogglePin: (app: AppRecord) => void;
  onRequestAccess: (app: AppRecord) => void;
  busy: boolean;
  preview: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  const dragProps = preview ? {} : { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style} {...dragProps} className="touch-none">
      <AppTile
        app={app}
        pinned
        variant="pinned"
        onTogglePin={onTogglePin}
        onRequestAccess={onRequestAccess}
        busy={busy}
        preview={preview}
      />
    </div>
  );
};

export const PinnedApps = ({
  pinnedApps,
  loading,
  busyId,
  readOnly,
  onTogglePin,
  onRequestAccess,
  onDragEnd,
}: PinnedAppsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <section aria-labelledby="pinned-heading">
      <h2 id="pinned-heading" className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Pinned
      </h2>
      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : pinnedApps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line p-6 text-center">
          <p className="text-sm text-text-secondary">No pinned apps yet.</p>
          <p className="mt-1 text-xs text-text-tertiary">
            Click the pin icon on any app below to add it here.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={readOnly ? undefined : onDragEnd}
        >
          <SortableContext items={pinnedApps.map((a) => a.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {pinnedApps.map((app) => (
                <SortablePinnedTile
                  key={app.id}
                  app={app}
                  onTogglePin={onTogglePin}
                  onRequestAccess={(a) => onRequestAccess(a)}
                  busy={busyId === app.id}
                  preview={readOnly}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
};
